import React, { useMemo, useState } from 'react';
import { Box, Typography, Grid, Modal, Paper, Link } from '@mui/material';
import CustomerModal from '../CustomerModal';
import { computeNegativityScore } from '../../utils/scoring';

// Collect all instances in a cell, and derive avg importance, mentions (count), and score
const collectDrawbacksCell = (data, solutionCategory, drawbackCategory) => {
  const instances = data
    .filter(
      (item) =>
        item.solution_choice_category === solutionCategory &&
        Array.isArray(item.drawbacks_of_solution_choice) &&
        item.drawbacks_of_solution_choice.some(
          (d) => d.drawback_category === drawbackCategory
        )
    )
    .flatMap((item) =>
      item.drawbacks_of_solution_choice
        .filter((d) => d.drawback_category === drawbackCategory)
        .map((d) => ({
          ...d,
          customer_name: item.customer_name,
          customer_data: item,
        }))
    );

  if (!instances.length) {
    return { avgImportance: null, mentions: 0, score: 0, items: [] };
  }

  const sumImp = instances.reduce((acc, d) => acc + (Number(d.drawback_importance) || 0), 0);
  const avgImportance = sumImp / instances.length;
  const mentions = instances.length;
  const score = computeNegativityScore({ averageImportance: avgImportance, mentions });

  return { avgImportance, mentions, score, items: instances };
};

const DrawbacksHeatmap = ({ filteredData }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedDrawbacks, setSelectedDrawbacks] = useState([]);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const solutionCategories = [...new Set(filteredData.map((item) => item.solution_choice_category))];
  const drawbackCategories = [
    ...new Set(
      filteredData.flatMap((item) =>
        (item.drawbacks_of_solution_choice || []).map((d) => d.drawback_category)
      )
    ),
  ];

  // Precompute score range for color scale using *scores*
  const { minScore, maxScore, cellMap } = useMemo(() => {
    const map = new Map();
    let min = Infinity;
    let max = -Infinity;

    for (const cat of drawbackCategories) {
      for (const sol of solutionCategories) {
        const cell = collectDrawbacksCell(filteredData, sol, cat);
        map.set(`${cat}__${sol}`, cell);
        if (cell.mentions > 0) {
          min = Math.min(min, cell.score);
          max = Math.max(max, cell.score);
        }
      }
    }
    if (min === Infinity) min = 0;
    if (max === -Infinity) max = 0;
    return { minScore: min, maxScore: max, cellMap: map };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, JSON.stringify(solutionCategories), JSON.stringify(drawbackCategories)]);

  // Reddish intensity by score
  const getCellColor = (score) => {
    if (!score || (maxScore === minScore)) return '#EAEAEA';
    const t = (score - minScore) / (maxScore - minScore);
    // Start near white → go redder
    const r = 255;
    const g = Math.round(255 - 155 * t);
    const b = Math.round(255 - 155 * t);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleCellClick = (items) => {
    if (items?.length) {
      setSelectedDrawbacks(items);
      setOpenModal(true);
    }
  };

  const handleClose = () => setOpenModal(false);
  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setCustomerModalOpen(true);
  };
  const handleCustomerModalClose = () => setCustomerModalOpen(false);

  return (
    <Box>
      {/* header row */}
      <Grid container>
        <Grid item xs={2}></Grid>
        {solutionCategories.map((solution, index) => (
          <Grid item xs key={`header-${index}`}>
            <Typography variant="subtitle2" align="center">{solution}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* body */}
      {drawbackCategories.map((drawbackCategory, rowIndex) => (
        <Grid container key={`row-${rowIndex}`}>
          <Grid item xs={2}>
            <Typography variant="subtitle2">{drawbackCategory}</Typography>
          </Grid>

          {solutionCategories.map((solutionCategory, colIndex) => {
            const { avgImportance, mentions, score, items } =
              cellMap.get(`${drawbackCategory}__${solutionCategory}`) || { avgImportance: null, mentions: 0, score: 0, items: [] };
            const cellColor = getCellColor(score);

            return (
              <Grid
                item
                xs
                key={`cell-${rowIndex}-${colIndex}`}
                style={{
                  backgroundColor: cellColor,
                  border: '1px solid #ccc',
                  padding: '8px',
                  textAlign: 'center',
                  cursor: items.length > 0 ? 'pointer' : 'default',
                }}
                onClick={() => handleCellClick(items)}
              >
                {mentions > 0 ? (
                  <>
                    {/* Primary number: Negativity Score */}
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Drawback score: {Math.round(score)}
                    </Typography>
                    {/* Subtext: avg importance & #convos */}
                    <Typography variant="caption" style={{ fontSize: '0.75rem' }}>
                      Importance {avgImportance.toFixed(2)} · {mentions} {mentions === 1 ? 'conversation' : 'conversations'}
                    </Typography>
                  </>
                ) : (
                  'n/a'
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}

      {/* modal */}
      <Modal open={openModal} onClose={handleClose}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '80%', bgcolor: 'background.paper', boxShadow: 24, p: 4, maxHeight: '80vh', overflowY: 'auto' }}>
          <Typography variant="h6">Drawback Details</Typography>
          {selectedDrawbacks.map((drawback, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                "{drawback.drawback_importance_quote}"
              </Typography>
              <Typography variant="h6" gutterBottom>{drawback.drawback}</Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Category: {drawback.drawback_category}</Typography>
                <Typography variant="body2">Importance: {drawback.drawback_importance}</Typography>
                <Typography variant="body2">Justification: {drawback.drawback_importance_justification}</Typography>
                <Link href="#" variant="body2" underline="hover" sx={{ color: 'secondary.main', display: 'block', mb: 1 }}>
                  Timestamps: {drawback.drawback_timestamps}
                </Link>
              </Box>

              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', color: 'primary.main', mt: 2 }}
                onClick={() => handleCustomerClick(drawback.customer_data)}
              >
                Customer: {drawback.customer_name}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Modal>

      <CustomerModal open={customerModalOpen} onClose={handleCustomerModalClose} customer={selectedCustomer} />
    </Box>
  );
};

export default DrawbacksHeatmap;
